import { Fragment, useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
    AlertTriangle, ArrowLeft, Check, ChevronDown, ChevronRight, IndianRupee, Loader2, MinusCircle,
    Undo2, X,
} from "lucide-react";

import { Button } from "../../components/ui/button";
import { DialogModal } from "../../components/ui/dialogModal";
import { Input } from "../../components/ui/input";
import { ContainerCard } from "../../components/ui/cards";
import useApiMethods from "../../lib/useApiMethods";
import ToastMsg from "../../components/common/ToastMsg";
import { checkUserPermissionFunc } from "../../components/common/CommonFunctions";
import { PERK_CANDIDATES, PERK_CRITERIA } from "../../constants/api";
import { cn } from "../../utils/tailwindUtils";

// Per-rule outcome styling. `no_data` is deliberately distinct from a fail: an
// employee with no published appraisal has not failed that condition, and
// showing the two the same way would hide a real gap in the evidence.
const OUTCOME = {
    pass: { label: "met", className: "text-emerald-700 dark:text-emerald-400", Icon: Check },
    fail: { label: "not met", className: "text-rose-700 dark:text-rose-400", Icon: X },
    no_data: { label: "no data", className: "text-amber-700 dark:text-amber-400", Icon: AlertTriangle },
    // A broken condition is a fault in the criteria, not a fact about the
    // employee, so it must not read as either a pass or a fail.
    invalid: { label: "invalid condition", className: "text-purple-700 dark:text-purple-400", Icon: AlertTriangle },
};

const METRIC_LABEL = {
    hours_ratio_percent: "Hours vs shift",
    appraisal_score: "Appraisal",
    late_days: "Late",
    early_out_days: "Early out",
    leave_days: "Leave",
    permission_count: "Permissions",
    overtime_hours: "Overtime",
    present_days: "Present",
};

const num = (value, suffix = "") =>
    value === null || value === undefined ? "-" : `${Number(value)}${suffix}`;

const PerkCandidateReview = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { fetch, update } = useApiMethods();

    const [criteria, setCriteria] = useState(null);
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [expanded, setExpanded] = useState({});
    const [filter, setFilter] = useState("all"); // all | selected | qualified | overridden | missing
    const [decision, setDecision] = useState(null); // { candidate, override }
    const [reason, setReason] = useState("");
    const [saving, setSaving] = useState(false);

    const { edit: editPermission } = checkUserPermissionFunc("payroll", "Perk Criteria", "SubMenu");

    const load = useCallback(async () => {
        setLoading(true);
        const [detail, candidates] = await Promise.all([
            fetch(PERK_CRITERIA, { id }),
            fetch(PERK_CANDIDATES, { id, page_size: 500 }),
        ]);
        setLoading(false);
        if (detail?.status === "success") setCriteria(detail.data);
        if (candidates?.status === "success") setRows(candidates.data?.result || []);
        else if (candidates?.status === "fail") ToastMsg("error", candidates.message);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    useEffect(() => {
        load();
    }, [load]);

    const counts = useMemo(() => {
        const selected = rows.filter((r) => r.is_selected).length;
        return {
            evaluated: rows.length,
            qualified: rows.filter((r) => r.auto_qualified).length,
            selected,
            overridden: rows.filter((r) => r.override).length,
            missing: rows.filter((r) => (r.evaluation?.missing_metrics || []).length).length,
        };
    }, [rows]);

    const visible = useMemo(() => {
        if (filter === "selected") return rows.filter((r) => r.is_selected);
        if (filter === "qualified") return rows.filter((r) => r.auto_qualified);
        if (filter === "overridden") return rows.filter((r) => r.override);
        if (filter === "missing") return rows.filter((r) => (r.evaluation?.missing_metrics || []).length);
        return rows;
    }, [rows, filter]);

    const submitDecision = async () => {
        if (!decision) return;
        setSaving(true);
        const response = await update(PERK_CANDIDATES, {
            candidate_id: decision.candidate.id,
            override: decision.override,
            reason,
        });
        setSaving(false);
        if (response?.status === "success") {
            ToastMsg("success", response.message);
            setDecision(null);
            setReason("");
            load();
        } else {
            ToastMsg("error", response?.message || "Could not record the decision");
        }
    };

    const clearDecision = async (candidate) => {
        setSaving(true);
        const response = await update(PERK_CANDIDATES, { candidate_id: candidate.id, override: null });
        setSaving(false);
        if (response?.status === "success") {
            ToastMsg("success", response.message);
            load();
        } else {
            ToastMsg("error", response?.message || "Could not clear the decision");
        }
    };

    const tiles = [
        { key: "all", label: "Evaluated", value: counts.evaluated },
        { key: "qualified", label: "Qualified by criteria", value: counts.qualified },
        { key: "selected", label: "On the final list", value: counts.selected, accent: true },
        { key: "overridden", label: "Reviewer changed", value: counts.overridden },
        { key: "missing", label: "Incomplete data", value: counts.missing, warn: counts.missing > 0 },
    ];

    if (loading) {
        return (
            <ContainerCard>
                <div className="flex items-center justify-center gap-2 py-20 text-muted-foreground">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Loading the shortlist
                </div>
            </ContainerCard>
        );
    }

    return (
        <ContainerCard>
            {/* Header */}
            <div className="flex flex-wrap items-start justify-between gap-3 p-3">
                <div className="min-w-0">
                    <Button
                        variant="ghost"
                        size="sm"
                        className="-ml-2 mb-1 text-muted-foreground"
                        onClick={() => navigate("/payroll/perkcriteria")}
                    >
                        <ArrowLeft className="h-4 w-4 mr-1" />
                        Perk Criteria
                    </Button>
                    <h1 className="text-xl font-semibold text-gray-900 dark:text-white truncate">
                        {criteria?.name || "Shortlist"}
                    </h1>
                    <p className="text-sm text-muted-foreground">
                        {criteria?.from_date} to {criteria?.to_date}
                        {criteria?.match_mode === "points"
                            ? ` · ${criteria?.min_points} points to qualify`
                            : criteria?.match_mode === "all"
                            ? " · all conditions"
                            : " · any condition"}
                    </p>
                </div>

                {/* The list is only worth something once it is paid, so the next
                    step is on the screen rather than left to be found. */}
                {editPermission && counts.selected > 0 && (
                    <Button onClick={() => navigate(`/payroll/perkcriteria/${id}/payout`)}>
                        <IndianRupee className="h-4 w-4 mr-1" />
                        Set the amounts
                    </Button>
                )}
            </div>

            {/* Summary tiles double as filters */}
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 px-3 pb-3">
                {tiles.map((tile) => (
                    <button
                        key={tile.key}
                        type="button"
                        onClick={() => setFilter(tile.key)}
                        className={cn(
                            "text-left rounded-lg border p-3 transition-colors",
                            filter === tile.key
                                ? "border-sidebar-primary bg-sidebar-primary/5"
                                : "border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/60 hover:bg-gray-100 dark:hover:bg-gray-800",
                        )}
                    >
                        <p className="text-[11px] uppercase tracking-wide text-muted-foreground">{tile.label}</p>
                        <p
                            className={cn(
                                "text-lg font-semibold tabular-nums",
                                tile.accent && "text-sidebar-primary",
                                tile.warn && "text-amber-700 dark:text-amber-400",
                                !tile.accent && !tile.warn && "text-gray-900 dark:text-white",
                            )}
                        >
                            {tile.value}
                        </p>
                    </button>
                ))}
            </div>

            {counts.missing > 0 && (
                <div className="mx-3 mb-3 rounded-lg border border-amber-300 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20 p-3">
                    <p className="text-xs text-amber-800 dark:text-amber-300">
                        {counts.missing} employee(s) were scored on incomplete data — usually no published appraisal.
                        Their score is out of a smaller total, so check those before signing off.
                    </p>
                </div>
            )}

            {/* Candidates */}
            <div className="mx-3 mb-3 overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700">
                <table className="min-w-full text-sm">
                    <thead className="bg-gray-50 dark:bg-gray-800">
                        <tr className="text-left text-xs uppercase tracking-wide text-muted-foreground">
                            <th className="px-2 py-2 w-8"></th>
                            <th className="px-3 py-2 font-medium">Employee</th>
                            <th className="px-3 py-2 font-medium text-right">Score</th>
                            <th className="px-3 py-2 font-medium">Why</th>
                            <th className="px-3 py-2 font-medium">Final</th>
                            <th className="px-3 py-2 font-medium text-right">Decision</th>
                        </tr>
                    </thead>
                    <tbody>
                        {visible.map((row) => {
                            const isOpen = !!expanded[row.id];
                            const ratio = row.points_possible
                                ? Math.min(100, (row.points / row.points_possible) * 100)
                                : 0;
                            return (
                                <Fragment key={row.id}>
                                    <tr
                                        className={cn(
                                            "border-t border-gray-200 dark:border-gray-700",
                                            row.is_selected && "bg-emerald-50/40 dark:bg-emerald-900/10",
                                        )}
                                    >
                                        <td className="px-2 py-2 align-top">
                                            <button
                                                type="button"
                                                aria-label={isOpen ? "Hide conditions" : "Show conditions"}
                                                onClick={() =>
                                                    setExpanded((prev) => ({ ...prev, [row.id]: !prev[row.id] }))
                                                }
                                                className="text-muted-foreground hover:text-foreground"
                                            >
                                                {isOpen ? (
                                                    <ChevronDown className="h-4 w-4" />
                                                ) : (
                                                    <ChevronRight className="h-4 w-4" />
                                                )}
                                            </button>
                                        </td>
                                        <td className="px-3 py-2 align-top">
                                            <span className="font-medium text-gray-900 dark:text-white">
                                                {row.employee_name || "-"}
                                            </span>
                                            <span className="block text-xs text-muted-foreground">
                                                {row.employee_code}
                                                {row.department ? ` · ${row.department}` : ""}
                                            </span>
                                        </td>
                                        <td className="px-3 py-2 align-top text-right">
                                            <span className="tabular-nums font-medium">
                                                {row.points}
                                                <span className="text-muted-foreground font-normal">
                                                    /{row.points_possible}
                                                </span>
                                            </span>
                                            <span className="mt-1 block h-1.5 w-24 ml-auto rounded-full bg-gray-200 dark:bg-gray-700 overflow-hidden">
                                                <span
                                                    className={cn(
                                                        "block h-full rounded-full",
                                                        row.auto_qualified ? "bg-emerald-500" : "bg-gray-400",
                                                    )}
                                                    style={{ width: `${ratio}%` }}
                                                />
                                            </span>
                                        </td>
                                        <td className="px-3 py-2 align-top max-w-[280px]">
                                            <span className="text-xs text-muted-foreground">{row.reason}</span>
                                        </td>
                                        <td className="px-3 py-2 align-top">
                                            <span
                                                className={cn(
                                                    "px-2 py-1 text-xs font-medium rounded-full whitespace-nowrap",
                                                    row.is_selected
                                                        ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300"
                                                        : "bg-gray-100 text-gray-600 dark:bg-gray-700/40 dark:text-gray-300",
                                                )}
                                            >
                                                {row.is_selected ? "on the list" : "not on the list"}
                                            </span>
                                            {row.override && (
                                                <span className="block mt-1 text-[11px] text-amber-700 dark:text-amber-400">
                                                    reviewer {row.override}d: {row.override_reason}
                                                </span>
                                            )}
                                        </td>
                                        <td className="px-3 py-2 align-top text-right whitespace-nowrap">
                                            {editPermission && (
                                                <>
                                                    {row.override ? (
                                                        <Button
                                                            variant="ghost"
                                                            size="sm"
                                                            title="Clear the decision"
                                                            className="text-muted-foreground"
                                                            onClick={() => clearDecision(row)}
                                                            disabled={saving}
                                                        >
                                                            <Undo2 className="h-4 w-4" />
                                                        </Button>
                                                    ) : (
                                                        <>
                                                            {!row.is_selected && (
                                                                <Button
                                                                    variant="ghost"
                                                                    size="sm"
                                                                    title="Add to the list"
                                                                    className="text-emerald-600 hover:bg-emerald-50 dark:hover:bg-gray-700"
                                                                    onClick={() => {
                                                                        setDecision({ candidate: row, override: "include" });
                                                                        setReason("");
                                                                    }}
                                                                >
                                                                    <Check className="h-4 w-4" />
                                                                </Button>
                                                            )}
                                                            {row.is_selected && (
                                                                <Button
                                                                    variant="ghost"
                                                                    size="sm"
                                                                    title="Take off the list"
                                                                    className="text-rose-600 hover:bg-rose-50 dark:hover:bg-gray-700"
                                                                    onClick={() => {
                                                                        setDecision({ candidate: row, override: "exclude" });
                                                                        setReason("");
                                                                    }}
                                                                >
                                                                    <MinusCircle className="h-4 w-4" />
                                                                </Button>
                                                            )}
                                                        </>
                                                    )}
                                                </>
                                            )}
                                        </td>
                                    </tr>

                                    {isOpen && (
                                        <tr className="bg-gray-50/70 dark:bg-gray-800/40">
                                            <td />
                                            <td colSpan={5} className="px-3 pb-3 pt-1">
                                                <div className="grid gap-3 md:grid-cols-2">
                                                    {/* Per-condition outcome: the answer to "why was he left out" */}
                                                    <div>
                                                        <p className="text-[11px] uppercase tracking-wide text-muted-foreground mb-1">
                                                            Conditions
                                                        </p>
                                                        <ul className="space-y-1">
                                                            {(row.evaluation?.details || []).map((d, i) => {
                                                                const o = OUTCOME[d.outcome] || OUTCOME.fail;
                                                                const Icon = o.Icon;
                                                                return (
                                                                    <li key={i} className="flex items-start gap-2 text-xs">
                                                                        <Icon className={cn("h-3.5 w-3.5 mt-0.5 shrink-0", o.className)} />
                                                                        <span>
                                                                            {d.description}
                                                                            {d.is_required && (
                                                                                <span className="ml-1 text-[10px] uppercase text-rose-600 dark:text-rose-400">
                                                                                    must pass
                                                                                </span>
                                                                            )}
                                                                            <span className="text-muted-foreground">
                                                                                {" — "}
                                                                                {d.outcome === "no_data"
                                                                                    ? "no data"
                                                                                    : d.outcome === "invalid"
                                                                                    ? "not a condition the system understands"
                                                                                    : `actual ${num(d.value)}`}
                                                                                {d.points_possible
                                                                                    ? ` · ${d.points_awarded}/${d.points_possible} pts`
                                                                                    : ""}
                                                                            </span>
                                                                        </span>
                                                                    </li>
                                                                );
                                                            })}
                                                        </ul>
                                                    </div>
                                                    {/* The raw record, so a reviewer can judge beyond the rules */}
                                                    <div>
                                                        <p className="text-[11px] uppercase tracking-wide text-muted-foreground mb-1">
                                                            Period record
                                                        </p>
                                                        <div className="grid grid-cols-2 gap-x-4 gap-y-0.5 text-xs">
                                                            {Object.keys(METRIC_LABEL).map((key) => (
                                                                <div key={key} className="flex justify-between gap-2">
                                                                    <span className="text-muted-foreground">
                                                                        {METRIC_LABEL[key]}
                                                                    </span>
                                                                    <span className="tabular-nums">
                                                                        {num(
                                                                            row.metrics?.[key],
                                                                            key === "hours_ratio_percent" ? "%" : "",
                                                                        )}
                                                                    </span>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    </div>
                                                </div>
                                            </td>
                                        </tr>
                                    )}
                                </Fragment>
                            );
                        })}
                        {!visible.length && (
                            <tr>
                                <td colSpan={6} className="px-3 py-10 text-center text-muted-foreground">
                                    No employees in this view.
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>

            {/* A reason is mandatory in both directions — this is the record that
                answers "why him and not her" when an employee asks. */}
            <DialogModal
                open={!!decision}
                onClose={() => {
                    setDecision(null);
                    setReason("");
                }}
                onSave={submitDecision}
                title={
                    decision?.override === "include"
                        ? `Add ${decision?.candidate?.employee_name} to the list`
                        : `Take ${decision?.candidate?.employee_name} off the list`
                }
                saveLabel={decision?.override === "include" ? "Add" : "Remove"}
                closeLabel="Cancel"
                loading={saving}
                width="480px"
                saveButtonColor={decision?.override === "exclude" ? "destructive" : undefined}
            >
                <div className="space-y-3">
                    <p className="text-sm text-muted-foreground">
                        {decision?.override === "include"
                            ? "The criteria did not select this employee. Record why they should be rewarded anyway."
                            : "The criteria selected this employee. Record why they should not be."}
                    </p>
                    <div className="space-y-2">
                        <label htmlFor="reason" className="text-xs font-medium text-gray-700 dark:text-gray-300">
                            Reason <span className="text-red-500">*</span>
                        </label>
                        <Input
                            id="reason"
                            value={reason}
                            onChange={(e) => setReason(e.target.value)}
                            placeholder="Covered the ward single-handed through August"
                            disabled={saving}
                            maxLength={255}
                        />
                        <p className="text-[11px] text-muted-foreground">
                            Kept on the record and shown alongside this decision from now on.
                        </p>
                    </div>
                </div>
            </DialogModal>
        </ContainerCard>
    );
};

export default PerkCandidateReview;
